/**
 * Shared vehicle photo upload widget used by VehicleFormPage (create/edit)
 * and the inline add-vehicle modal on LeaseDetailPage.
 *
 * Exported:
 *   vehiclePhotoUrl(key)      — resolve bare UUID or /api/images/... to a displayable URL
 *   uploadVehiclePhoto(file)  — presigned-URL upload; returns bare UUID key
 *   VehiclePhotoUploadProps   — prop interface
 *   VehiclePhotoUpload        — React component (named export)
 */
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { api } from '@/lib/api'

const API_BASE = import.meta.env.VITE_API_URL || ''

/** Resolve a bare UUID key or an /api/images/... path to a displayable URL. */
// eslint-disable-next-line react-refresh/only-export-components -- tested helper co-located with the component
export function vehiclePhotoUrl(key: string): string {
  if (key.startsWith('/api/images/') || key.startsWith('http')) {
    return key.startsWith('/api/') ? `${API_BASE}${key}` : key
  }
  return `${API_BASE}/api/images/${key}`
}

/**
 * Upload a single image via presigned URL.
 * Returns the bare UUID key (stored as-is in the vehicle payload).
 */
// eslint-disable-next-line react-refresh/only-export-components -- tested helper co-located with the component
export async function uploadVehiclePhoto(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return presigned.key
}

/** Single-photo upload widget used for front/rear vehicle photos. */
export interface VehiclePhotoUploadProps {
  label: string
  hint?: string
  photoKey: string | null
  uploading: boolean
  error?: string
  inputRef: React.RefObject<HTMLInputElement | null>
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemove: () => void
  'data-testid'?: string
}

export function VehiclePhotoUpload({
  label,
  hint,
  photoKey,
  uploading,
  error,
  inputRef,
  onFileChange,
  onRemove,
  'data-testid': testId,
}: VehiclePhotoUploadProps) {
  const { t: tc } = useTranslation('common')

  return (
    <div className="space-y-2 min-w-0" data-testid={testId}>
      <label className="label-text font-medium">{label}</label>
      {hint && <p className="text-xs text-base-content/60">{hint}</p>}

      {photoKey ? (
        <div className="relative w-32 h-24 rounded-lg overflow-hidden bg-base-200 group">
          <img
            src={vehiclePhotoUrl(photoKey)}
            alt={label}
            className="w-full h-full object-cover"
            data-testid={testId ? `${testId}-preview` : undefined}
          />
          <Button
            type="button"
            onClick={onRemove}
            aria-label={tc('actions.remove')}
            variant="error"
            shape="circle"
            size="xs"
            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            ×
          </Button>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            uploading ? 'border-base-300 bg-base-200' : 'border-base-300 hover:border-primary'
          }`}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onFileChange}
            className="hidden"
            disabled={uploading}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          {uploading ? (
            <span className="text-sm text-base-content/60">{tc('file_upload.uploading')}</span>
          ) : (
            <span className="text-sm text-base-content/60">{tc('file_upload.click_to_upload')}</span>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-error text-sm">
          {error}
        </p>
      )}
    </div>
  )
}
